import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Phone, Mail, MapPin, Check, Loader2, Gift, Shield } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useTranslation } from "../i18n";
import { LanguageSelector } from "../components/LanguageSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const sessionId = searchParams.get('session');
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countries, setCountries] = useState([]);
  const [formData, setFormData] = useState({
    email: "",
    country: "",
    city: "",
    phone_number: "",
    phone_country_code: "",
    consent: false,
    honeypot: "",
    referral_code: referralCode || ""
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCountries();
    autoDetectCountry();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/countries`);
      const data = await response.json();
      setCountries(data.all || []);
    } catch (error) {
      setCountries([
        "Nigeria", "Ghana", "Kenya", "South Africa", "Egypt", "Morocco",
        "Cameroon", "Senegal", "Algeria", "Tanzania", "Uganda", "Zimbabwe",
        "United Kingdom", "United States", "Other"
      ]);
    }
  };

  const autoDetectCountry = async () => {
    try {
      const response = await fetch(`${API_URL}/api/geolocate`);
      const data = await response.json();
      if (data.country) {
        setFormData(prev => ({
          ...prev,
          country: prev.country || data.country,
          city: prev.city || data.city || ""
        }));
      }
    } catch {
      // geolocation not available
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.country) {
      newErrors.country = "Country is required";
    }
    if (formData.phone_number && !/^\+?[\d\s-]{7,}$/.test(formData.phone_number)) {
      newErrors.phone_number = "Please enter a valid phone number";
    }
    if (!formData.consent) {
      newErrors.consent = "You must agree to the terms to continue";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    
    setLoading(true);
    
    try {
      if (sessionId) {
        // New flow: register after survey
        const response = await fetch(`${API_URL}/api/register-after-survey`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, session_id: sessionId }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || "Registration failed");
        }
        
        // Store for verification page
        localStorage.setItem("registration", JSON.stringify({
          respondent_id: data.respondent_id,
          referral_code: data.referral_code,
          email: data.email
        }));
        
        setSubmitted(true);
        toast.success("Verification email sent! Check your inbox.");
      } else {
        // Legacy flow: register first, then survey
        const response = await fetch(`${API_URL}/api/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || "Registration failed");
        }
        
        localStorage.setItem("registration", JSON.stringify({
          respondent_id: data.respondent_id,
          email: formData.email,
          country: formData.country,
          city: formData.city,
          phone_number: formData.phone_number
        }));
        
        toast.success("Registration successful!");
        navigate(`/survey/${data.respondent_id}`);
      }
    } catch (error) {
      toast.error(error.message || "Failed to register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Show "check your email" state after submit
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
              </Link>
            </div>
          </div>
        </header>
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#FBBF24]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-[#FBBF24]" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="verify-pending-headline">
              {t("register.checkEmail")}
            </h1>
            <p className="text-gray-400 text-lg mb-6">
              {t("register.checkEmailMsg").split(formData.email)[0]}<span className="text-[#FBBF24] font-bold">{formData.email}</span>
            </p>
            <p className="text-gray-500 mb-8">
              {t("register.checkEmailMsg")}
            </p>
            <div className="bg-[#141414] border border-white/10 p-6 rounded-sm">
              <p className="text-gray-400 text-sm">
                {t("register.didntReceive")}{" "}
                <button 
                  onClick={() => setSubmitted(false)} 
                  className="text-[#FBBF24] hover:underline"
                  data-testid="try-again-button"
                >
                  {t("register.tryAgain")}
                </button>
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/"
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="back-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("register.returnHome")}
            </Link>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-xl mx-auto">
          {/* Referral Banner */}
          {referralCode && (
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-4 rounded-sm mb-6 flex items-center gap-3">
              <Gift className="w-5 h-5 text-[#10B981] flex-shrink-0" />
              <p className="text-[#10B981] text-sm">
                {t("register.referralBanner")}
              </p>
            </div>
          )}
          
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-[#FBBF24] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              {sessionId ? t("register.finalStep") : t("register.step1")}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="register-headline">
              {sessionId ? t("register.headlineValidate") : t("register.headlineJoin")}
            </h1>
            <p className="text-gray-400">
              {sessionId ? t("register.subtitleValidate") : t("register.subtitleJoin")}
            </p>
          </div>

          {/* Trust indicators */}
          {sessionId && (
            <div className="bg-[#141414] border border-white/10 p-4 rounded-sm mb-6 flex items-center gap-3">
              <Shield className="w-5 h-5 text-[#10B981] flex-shrink-0" />
              <p className="text-gray-400 text-sm">
                <span className="text-white font-medium">{t("register.whyVerify")}</span> {t("register.trust")}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot field */}
            <input
              type="text"
              name="website"
              value={formData.honeypot}
              onChange={(e) => handleChange("honeypot", e.target.value)}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FBBF24]" />
                {t("register.email")} <span className="text-red-500">{t("register.required")}</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] focus:ring-[#FBBF24] h-12"
                data-testid="register-email-input"
              />
              {errors.email && (
                <p className="text-red-500 text-sm" data-testid="register-email-error">{errors.email}</p>
              )}
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label htmlFor="country" className="text-gray-300 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#FBBF24]" />
                {t("survey.country")} <span className="text-red-500">{t("register.required")}</span>
              </Label>
              <Select 
                value={formData.country} 
                onValueChange={(value) => handleChange("country", value)}
              >
                <SelectTrigger 
                  className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] focus:ring-[#FBBF24] h-12"
                  data-testid="register-country-select"
                >
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1C1C] border-white/20 max-h-60">
                  {countries.map((country) => (
                    <SelectItem 
                      key={country} 
                      value={country}
                      className="text-white hover:bg-white/10 focus:bg-white/10"
                    >
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.country && (
                <p className="text-red-500 text-sm" data-testid="register-country-error">{errors.country}</p>
              )}
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label htmlFor="city" className="text-gray-300 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                {t("survey.city")} <span className="text-gray-500">{t("register.optional")}</span>
              </Label>
              <Input
                id="city"
                type="text"
                placeholder="Your city"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] focus:ring-[#FBBF24] h-12"
                data-testid="register-city-input"
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-300 flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-500" />
                {t("register.phone")} <span className="text-gray-500">{t("register.optional")}</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+234 xxx xxx xxxx"
                value={formData.phone_number}
                onChange={(e) => handleChange("phone_number", e.target.value)}
                className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] focus:ring-[#FBBF24] h-12"
                data-testid="register-phone-input"
              />
              {errors.phone_number && (
                <p className="text-red-500 text-sm">{errors.phone_number}</p>
              )}
              <p className="text-gray-500 text-sm">
                {t("register.phoneNote")}
              </p>
            </div>

            {/* Prize Draw Info */}
            <div className="bg-[#FBBF24]/10 border border-[#FBBF24]/30 p-4 rounded-sm">
              <p className="text-[#FBBF24] font-bold text-sm mb-2">
                {t("register.prizeTitle")}
              </p>
              <p className="text-gray-300 text-sm">
                {t("register.prizeInfo")}
              </p>
            </div>

            {/* Consent */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={formData.consent === true}
                onCheckedChange={(checked) => handleChange("consent", !!checked)}
                className="mt-1 border-white/20 data-[state=checked]:bg-[#FBBF24] data-[state=checked]:border-[#FBBF24]"
                data-testid="register-consent-checkbox"
              />
              <Label htmlFor="consent" className="text-gray-400 text-sm leading-relaxed cursor-pointer select-none">
                {t("register.consent")}{" "}
                <Link 
                  to="/privacy" 
                  className="text-[#FBBF24] hover:underline ml-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t("register.privacyLink")}
                </Link>
              </Label>
            </div>
            {errors.consent && (
              <p className="text-red-500 text-sm" data-testid="register-consent-error">{errors.consent}</p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {sessionId ? t("register.sending") : t("register.registering")}
                </>
              ) : (
                <>
                  {sessionId ? t("register.submitValidate") : t("register.submitContinue")}
                  {sessionId ? <Check className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                </>
              )}
            </button>
          </form>

          {/* Already registered? */}
          <p className="text-center text-gray-500 mt-8 text-sm">
            {t("register.already")}{" "}
            <Link to="/" className="text-[#FBBF24] hover:underline">
              {t("register.returnHome")}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
